<template>
  <ClientOnly>
    <Teleport
      :to="teleportTarget"
      :disabled="!hasTarget"
    >
      <!-- Backdrop Transition -->
      <transition
        enter-active-class="transition-opacity duration-200"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="isOpen"
          class="fixed transition-all duration-75 z-[45]"
          :class="[backdropClasses, overlayStyle ? '' : 'inset-0']"
          :style="overlayStyle"
        />
      </transition>

      <!-- Modal Transition -->
      <transition
        enter-active-class="transition-all duration-125 ease-out"
        enter-from-class="opacity-0 -translate-y-4"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-200 ease-in"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 -translate-y-4"
      >
        <div
          v-if="isOpen"
          ref="modalRef"
          data-test-id="am3modal"
          class="fixed z-[55] overflow-y-auto modal-container"
          :class="overlayStyle ? '' : 'inset-0'"
          :style="overlayStyle"
        >
          <div
            class="flex min-h-full items-center text-sm justify-center p-4 text-center sm:items-center sm:p-0 mx-auto"
            :class="sizeClasses"
            @click.self.stop
          >
            <div
              ref="panelRef"
              class="relative transform overflow-hidden rounded-lg text-left align-middle shadow-xl transition-all w-full mx-auto max-w-full sm:my-8 flex flex-col max-h-[90vh]"
              :class="modalClasses"
            >
              <!-- Header -->
              <div
                v-if="!hideHeader"
                class="flex items-start justify-between py-4 px-4 md:px-6 text-xl flex-shrink-0"
                data-test-id="modal-header"
              >
                <slot name="header">
                  <div v-if="headerTitle || headerDescription">
                    <div
                      v-if="headerTitle"
                      data-test-id="modal-header--title"
                      class="text-lg sm:text-xl font-semibold text-font-primary"
                    >
                      {{ headerTitle }}
                    </div>
                    <div
                      v-if="headerDescription"
                      data-test-id="modal-header--description"
                      class="mt-1 mb-1 text-sm text-font-secondary"
                    >
                      {{ headerDescription }}
                    </div>
                  </div>
                  <!-- Fallback to old header prop for backwards compatibility -->
                  <h3
                    v-else
                    class="text-xl font-semibold text-font-primary"
                  >
                    {{ header }}
                  </h3>
                </slot>
                <button
                  v-if="hasCrossIcon"
                  class="rounded-full size-8 outline-offset-2 outline outline-1 outline-transparent focus-visible:outline-brand relative hover:bg-surfaces-neutral hover:text-brand transition-colors duration-100 flex flex-row items-center justify-center flex-shrink-0"
                  :data-test-id="generateDataTestId(dataTestIdValue, 'modal', 'close')"
                  @click="handleClose"
                >
                  <span class="[@media(pointer:fine)]:hidden top-1/3 left-1/3 -translate-x-1/3 -translate-y-1/3 size-10 absolute" />
                  <AM3Icon
                    type="x-mark"
                    size="lg"
                    color="strong"
                  />
                </button>
              </div>

              <slot name="search" />

              <!-- Body -->
              <div
                ref="body"
                v-ar-shadow-scroll
                class="am3-modal--body"
                :class="bodyClass"
                @scroll="emit('scroll', $event)"
              >
                <slot name="body">
                  <slot /> <!-- fallback slot for backwards compatibility -->
                </slot>
              </div>

              <!-- Footer -->
              <div
                v-if="!hideFooter"
                class="flex justify-end gap-3 px-4 py-4 md:p-6 flex-shrink-0 items-center"
              >
                <slot name="footer">
                  <AM3Button
                    text="Close"
                    type="tertiary"
                    :data-test-id="generateDataTestId(dataTestIdValue, 'modal', 'cancel-button')"
                    @click="handleClose"
                  />
                </slot>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </ClientOnly>
</template>

<script setup lang="ts">
import { computed, nextTick, useTemplateRef, onMounted, onUnmounted, ref, watch } from 'vue'
import { useMutationObserver, useResizeObserver } from '@vueuse/core'
import { useEscapeToClose } from '@/composables/useEscapeToClose'

import AM3Icon from '@/components/am3-generic/base/icon/AM3Icon.vue'
import AM3Button from '@/components/am3-generic/base/AM3Button.vue'
import { generateDataTestId } from '@/components/am3-generic/core/dataTestId'

type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl'
type ModalTheme = 'dark' | 'light'

const isOpen = defineModel<boolean>('isOpen', { required: true })
const {
  header = '',
  headerTitle,
  headerDescription,
  width = 'md',
  hasCrossIcon = true,
  hideHeader = false,
  hideFooter = false,
  closeOnClickOutside = false,
  dataTestId,
  theme = 'dark',
  backdropBlur = false,
  teleportTo = '#modal',
  bodyClass = `overflow-y-auto max-h-96 text-sm lg:max-h-[70vh] pb-2 [&_p]:text-sm`,
  overlayTargetId,
} = defineProps<{
  header?: string
  headerTitle?: string
  headerDescription?: string
  width?: ModalSize
  hasCrossIcon?: boolean
  hideHeader?: boolean
  hideFooter?: boolean
  closeOnClickOutside?: boolean
  dataTestId?: string
  theme?: ModalTheme
  backdropBlur?: boolean
  teleportTo?: string
  bodyClass?: string
  /**
   * ID of an element in the page. When provided, the backdrop and the modal
   * panel are confined to that element's on-screen bounds instead of the
   * full viewport (e.g. to leave a preview pane outside the modal untouched).
   */
  overlayTargetId?: string
}>()

const emit = defineEmits<{
  // TODO remove close event needed since `isOpen` is now a model
  close: []
  scroll: [event: Event]
}>()

const dataTestIdValue = computed(() => dataTestId || header)

const sizeClasses = computed(() => ({
  xs: 'max-w-[375px]',
  sm: 'max-w-screen-sm',
  md: 'max-w-screen-sm lg:max-w-screen-md',
  lg: 'max-w-screen-sm lg:max-w-screen-md xl:max-w-[992px]',
  xl: 'max-w-screen-sm lg:max-w-screen-md xl:max-w-[1200px]',
}[width]))

const modalRef = ref<HTMLElement | null>(null)
const mouseDownOutside = ref(false)

const bodyRef = useTemplateRef<HTMLElement>('body')

/*
* @constant panelRef is used to track the height of the modal content and animate height changes smoothly.
* @type {Ref<HTMLElement | null>}
*/

const panelRef = useTemplateRef<HTMLElement>('panelRef')

let panelNaturalHeight: number | null = null
let panelAnimating = false
let panelTransitionTimeout: ReturnType<typeof setTimeout> | null = null

// Reset state when the panel unmounts (modal closes)
watch(panelRef, (el) => {
  if (!el) {
    panelNaturalHeight = null
    panelAnimating = false
  }
})

// Track natural height without animating
useResizeObserver(panelRef, (entries) => {
  if (panelAnimating) return
  const h = entries[0]?.borderBoxSize?.[0]?.blockSize ?? entries[0]?.contentRect.height
  if (h) panelNaturalHeight = h
})

// MutationObserver fires before paint — pin the old height before the browser
// renders the new layout, so there is no snap-back flicker.
useMutationObserver(panelRef, () => {
  const el = panelRef.value
  if (!el || panelAnimating || panelNaturalHeight === null) return

  // Start the animation with the old height, so we can transition to the new height smoothly
  const fromHeight = panelNaturalHeight
  panelAnimating = true
  el.style.height = `${fromHeight}px`

  requestAnimationFrame(() => {
    el.style.height = ''
    const toHeight = el.offsetHeight // force layout to measure new height

    if (Math.abs(toHeight - fromHeight) < 2) {
      panelAnimating = false
      return
    }

    el.style.height = `${fromHeight}px`
    requestAnimationFrame(() => {
      el.style.transition = 'height 125ms ease-in-out'
      el.style.height = `${toHeight}px`

      if (panelTransitionTimeout) clearTimeout(panelTransitionTimeout)
      panelTransitionTimeout = setTimeout(() => {
        el.style.height = ''
        el.style.transition = ''
        panelNaturalHeight = toHeight
        panelAnimating = false
      }, 140)
    })
  })
}, { childList: true, subtree: true, attributes: false, characterData: false })

const backdropClasses = computed((): string[] => {
  if (!['dark', 'light'].includes(theme)) {
    throw new Error(`Unknown modal theme: ${theme}`)
  }
  const bgClass = theme === 'dark' ? 'bg-black/40' : 'bg-white/25'
  return [
    bgClass,
    ...(backdropBlur ? ['backdrop-blur-[2px]', 'filter'] : []),
  ]
})

const modalClasses = computed((): string[] => {
  const classes = ['bg-white'] // Default background
  // Apply any modal-specific styling based on theme
  if (theme === 'light') {
    // For light theme, we might want to adjust shadows or borders
    classes.push('border border-surfaces-neutral')
  }
  return classes
})

useEscapeToClose(isOpen, handleClose)

// Default fallback if no suitable target is found
const teleportTarget = computed((): string | null => teleportTo || '#modal')

function handleClose() {
  isOpen.value = false
  emit('close')
}

function disableDocumentScroll() {
  document.body.style.overflow = 'hidden'
}

function enableDocumentScroll() {
  document.body.style.overflow = 'auto'
}

watch(isOpen, (isOpen) => {
  if (isOpen) {
    disableDocumentScroll()
  }
  else {
    enableDocumentScroll()
  }
}, { immediate: true })

// Confines the backdrop + panel to a single element's on-screen bounds
// (rather than the full viewport) when `overlayTargetId` is provided.
const overlayTargetEl = ref<HTMLElement | null>(null)
const overlayTargetRect = ref<{ top: number, left: number, width: number, height: number } | null>(null)

const overlayStyle = computed(() => {
  const rect = overlayTargetRect.value
  if (!rect) return undefined
  return {
    top: `${rect.top}px`,
    left: `${rect.left}px`,
    width: `${rect.width}px`,
    height: `${rect.height}px`,
  }
})

function updateOverlayTarget() {
  if (!overlayTargetId || typeof document === 'undefined') {
    overlayTargetEl.value = null
    overlayTargetRect.value = null
    return
  }
  overlayTargetEl.value = document.getElementById(overlayTargetId)
  const rect = overlayTargetEl.value?.getBoundingClientRect()
  overlayTargetRect.value = rect
    ? { top: rect.top, left: rect.left, width: rect.width, height: rect.height }
    : null
}

useResizeObserver(overlayTargetEl, updateOverlayTarget)

// Only listen/poll while the modal is actually open, so a mounted-but-closed
// modal doesn't force a layout on every scroll anywhere on the page, and any
// in-flight poll is torn down as soon as the modal closes (or unmounts).
let overlayPollInterval: ReturnType<typeof setInterval> | null = null

function stopOverlayPolling() {
  if (overlayPollInterval) {
    clearInterval(overlayPollInterval)
    overlayPollInterval = null
  }
}

watch(isOpen, (open) => {
  if (!overlayTargetId) return

  if (!open) {
    window.removeEventListener('scroll', updateOverlayTarget, true)
    window.removeEventListener('resize', updateOverlayTarget)
    stopOverlayPolling()
    return
  }

  window.addEventListener('scroll', updateOverlayTarget, true)
  window.addEventListener('resize', updateOverlayTarget)
  updateOverlayTarget()
  // Target element may not be rendered yet on the same tick the modal opens.
  if (!overlayTargetEl.value) {
    const maxAttempts = 50
    let attempts = 0
    overlayPollInterval = setInterval(() => {
      attempts++
      updateOverlayTarget()
      if (overlayTargetEl.value || attempts >= maxAttempts) stopOverlayPolling()
    }, 10)
  }
}, { immediate: true })

const hasTarget = ref(false)

function checkTarget() {
  if (typeof document !== 'undefined') {
    const target = teleportTarget.value
    try {
      if (target && target.startsWith('#')) {
        const element = document.getElementById(target.slice(1))
        hasTarget.value = !!element
        if (!element) {
          console.warn(`AM3Modal: Target element '${target}' not found in DOM`)
        }
      }
      else if (target) {
        const element = document.querySelector(target)
        hasTarget.value = !!element
        if (!element) {
          console.warn(`AM3Modal: Target element '${target}' not found in DOM`)
        }
      }
      else {
        hasTarget.value = false
      }
    }
    catch (error) {
      console.error('AM3Modal: Error checking teleport target:', error)
      hasTarget.value = false
    }
  }
}

// Same pattern as the overlay-target poll above: stored outside the closure so
// it can be torn down on unmount instead of running to completion regardless
// of whether the component is still mounted.
let teleportPollInterval: ReturnType<typeof setInterval> | null = null

function stopTeleportPolling() {
  if (teleportPollInterval) {
    clearInterval(teleportPollInterval)
    teleportPollInterval = null
  }
}

onMounted(() => {
  // Add multiple timing checks to ensure target is ready
  const ensureTarget = () => {
    checkTarget()
    if (!hasTarget.value) {
      // Keep checking every 10ms for up to 500ms
      const maxAttempts = 50
      let attempts = 0

      stopTeleportPolling()
      teleportPollInterval = setInterval(() => {
        attempts++
        checkTarget()

        if (hasTarget.value || attempts >= maxAttempts) {
          stopTeleportPolling()
          if (!hasTarget.value) {
            console.warn('AM3Modal: Could not find teleport target after multiple attempts')
          }
        }
      }, 10)
    }
  }

  // Initial check
  ensureTarget()

  // Use nextTick to ensure DOM is fully rendered
  nextTick(() => {
    ensureTarget()
  })
})

watch([isOpen, modalRef], ([isOpen, modalRef]) => {
  if (isOpen && modalRef) {
    modalRef.focus()
    // Focus the first input element inside the modal, preferring [data-focus-first]
    nextTick(() => {
      const marked = modalRef.querySelector('[data-focus-first]')
      const firstInput = marked
        ? marked.querySelector('button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled])')
        : modalRef.querySelector('input, textarea, select, [contenteditable="true"]')
      if (firstInput) {
        (firstInput as HTMLElement).focus()
      }
    })
  }
}, { immediate: true })

// Watch for modal opening to recheck target
watch(isOpen, (newValue) => {
  if (newValue) {
    nextTick(() => {
      checkTarget()
    })
  }
})

onUnmounted(() => {
  enableDocumentScroll()
  if (panelTransitionTimeout) clearTimeout(panelTransitionTimeout)
  if (overlayTargetId) {
    window.removeEventListener('scroll', updateOverlayTarget, true)
    window.removeEventListener('resize', updateOverlayTarget)
  }
  stopOverlayPolling()
  stopTeleportPolling()
})
</script>

<style scoped>
.enter-active {
  transition-timing-function: ease-out;
  transition-duration: 300ms;
}

.leave-active {
  transition-timing-function: ease-in;
  transition-duration: 200ms;
}
</style>

<style>
.modal-container .am3-tabs-header {
  padding-left: .85rem;
}

.modal-container .am3-tabs-header + div {
  padding-left: 1rem;
  padding-right: 1rem;
}

.am3-modal--body:not(:has(.am3-tabs-header)) > div {
  padding-left: 1rem;
  padding-right: 1rem;
}

.am3-modal--body:has(.am3-email-preview) > div {
  padding-left: 0 ;
  padding-right: 0 ;
}

@media (min-width: 640px) {

  .modal-container .am3-tabs-header + div {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .am3-modal--body:not(:has(.am3-tabs-header)) > div {
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  .am3-modal--body:has(.am3-email-preview) > div {
    padding-left: 0 ;
    padding-right: 0 ;
  }

}
</style>
